import { Dispatch, Reducer, useCallback, useReducer, useRef } from "react";

export type Thunk<A, S> = (d: Dispatch<A>, getState: () => S) => void;

/**
 *
 * A Hook similar to `useReducer`, except that the returned `dispatch` function accepts a thunk as well as a normal action
 *
 * "Thunk" is a Redux concept. It can be used to dispatch actions after an async operation is done. https://redux.js.org/usage/writing-logic-thunks
 *
 * @param reducer
 * @param initialState
 * @returns [state, dispatch]
 */
export function useThunkReducer<S, A>(
  reducer: Reducer<S, A>,
  initialState: S
): [S, Dispatch<A | Thunk<A, S>>] {
  const lastState = useRef(initialState);
  const getState = useCallback(() => lastState.current, []);
  const [state, dispatch] = useReducer(
    (s: S, a: A) => (lastState.current = reducer(s, a)),
    initialState
  );

  const dispatchWithThunk = useCallback(
    (action: A | Thunk<A, S>) => {
      return typeof action === "function"
        ? (action as Thunk<A, S>)(dispatch, getState)
        : dispatch(action);
    },
    [getState, dispatch]
  );

  return [state, dispatchWithThunk];
}
